import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatAppointmentDateTime } from '../../lib/appointmentTime';
import { bookingStatusReasonKey } from '../../lib/bookingLifecycle';
import BookingActions, { type BookingActor } from './BookingActions';
import BookingStatusPill from './BookingStatusPill';
import s from './BookingDetail.module.css';

/**
 * O1 (AB#2199, SO-06) — one booking as both the Calendar and the Appointments list
 * describe it. Adapters build it from a CalendarAppointmentDto or an owner list row,
 * so the dialog below is the same experience from either entry point.
 */
export interface BookingDetailModel {
    id: number;
    serviceName: string;
    status: number | string;
    cancellationSource?: string | null;
    allowedTransitions?: string[] | null;
    canMarkPaid?: boolean;
    paymentStatus?: string | null;
    /** Local booking date "yyyy-MM-dd". */
    date?: string | null;
    /** "HH:mm" local. */
    startTime: string;
    endTime?: string | null;
    customerName?: string | null;
    customerPhone?: string | null;
    staffName?: string | null;
    branchName?: string | null;
    amount?: number | null;
    currencyIso?: string | null;
    bundleName?: string | null;
    isBundle?: boolean;
    /** S1 (AB#2204) — booking notes (staff detail, own bookings only). */
    notes?: string | null;
}

interface BookingDetailDialogProps {
    booking: BookingDetailModel;
    onClose: () => void;
    /** Shown as "Mark as paid" only when the backend says the booking can be paid. */
    onMarkPaid?: () => void;
    /**
     * Extra footer action, e.g. "Open in calendar" from the Appointments list.
     * Owner-only: never rendered for `actor="staff"`.
     */
    footerAction?: React.ReactNode;
    /**
     * S1 (AB#2204, SG-05) — `staff` = a staff member's own booking: actions go
     * through the staff endpoint and owner-only controls (Open in calendar,
     * reschedule, reassignment) are never shown. Default `owner`.
     */
    actor?: BookingActor;
    /** Optional note under the head, e.g. "couldn't load the latest details". */
    notice?: string | null;
}

const PAYMENT_KEY: Record<string, string> = {
    Paid: 'booking.payment.paid',
    Unpaid: 'booking.payment.unpaid',
    Refunded: 'booking.payment.refunded',
};

/** Full local date of a booking, e.g. "Tue, 4 Nov 2026" / Arabic equivalent. */
export function formatBookingDate(date: string | null | undefined, startTime: string, language: string): string | null {
    if (!date) return null;
    const iso = `${date.slice(0, 10)}T${startTime || '00:00'}:00Z`;
    return formatAppointmentDateTime(iso, language, {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
}

const BookingDetailDialog: React.FC<BookingDetailDialogProps> = ({
    booking, onClose, onMarkPaid, footerAction, actor = 'owner', notice,
}) => {
    const { t, language } = useLanguage();
    const dialogRef = useRef<HTMLDivElement>(null);

    // a11y — move focus into the dialog on open and give it back on close (the
    // Appointments row / Calendar card the owner opened it from).
    useEffect(() => {
        const opener = document.activeElement as HTMLElement | null;
        dialogRef.current?.focus();
        return () => { opener?.focus?.(); };
    }, []);

    const reasonKey = bookingStatusReasonKey(booking.status, booking.cancellationSource);
    const dateLabel = formatBookingDate(booking.date, booking.startTime, language);
    const timeLabel = booking.endTime ? `⁨${booking.startTime} – ${booking.endTime}⁩` : `⁨${booking.startTime}⁩`;
    const paymentKey = booking.paymentStatus ? PAYMENT_KEY[booking.paymentStatus] : undefined;

    const rows: { label: string; value: React.ReactNode; ltr?: boolean }[] = [
        { label: t('booking.detail.customer'), value: booking.customerName || t('booking.detail.walkIn') },
        ...(booking.customerPhone ? [{ label: t('booking.detail.phone'), value: booking.customerPhone, ltr: true }] : []),
        ...(dateLabel ? [{ label: t('booking.detail.date'), value: dateLabel }] : []),
        { label: t('booking.detail.time'), value: timeLabel },
        ...(booking.staffName ? [{ label: t('booking.detail.staff'), value: booking.staffName }] : []),
        ...(booking.branchName ? [{ label: t('booking.detail.branch'), value: booking.branchName }] : []),
        // Notes are free text (Arabic or English) — let the browser pick the direction.
        ...(booking.notes ? [{ label: t('stf.detail.notes'), value: <bdi dir="auto">{booking.notes}</bdi> }] : []),
        ...(paymentKey ? [{
            label: t('booking.detail.payment'),
            value: booking.amount != null
                ? <>{t(paymentKey)} · <span dir="ltr">{booking.amount} {booking.currencyIso ?? ''}</span></>
                : t(paymentKey),
        }] : []),
    ];

    return (
        <div className={s.backdrop} role="presentation" onClick={onClose}>
            <div
                ref={dialogRef}
                className={s.dialog}
                role="dialog"
                aria-modal="true"
                aria-label={t('booking.detail.title')}
                data-testid="booking-detail"
                tabIndex={-1}
                onClick={e => e.stopPropagation()}
                // Escape closes (keyboard parity with the backdrop click); an open
                // Decline / Cancel confirm step handles Escape first.
                onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
            >
                <div className={s.head}>
                    <h3 className={s.title}>{booking.serviceName}</h3>
                    <BookingStatusPill status={booking.status} cancellationSource={booking.cancellationSource} />
                </div>
                {reasonKey && <p className={s.reason}>{t(reasonKey)}</p>}
                {notice && <p className={s.reason} role="status">{notice}</p>}
                {/* AB#2034 — this booking is one leg of a customer package bundle. */}
                {booking.isBundle && (
                    <div className={s.bundleNote}>
                        <span className={s.bundleNoteTag}>{t('bundle.tag')}</span>
                        {booking.bundleName && (
                            <span className={s.bundleNoteName}>
                                {t('bundle.partOf').replace('{name}', '⁨' + booking.bundleName + '⁩')}
                            </span>
                        )}
                    </div>
                )}
                <div className={s.body}>
                    {rows.map(r => (
                        <div className={s.row} key={r.label}>
                            <span className={s.rowLabel}>{r.label}</span>
                            <span className={s.rowValue} dir={r.ltr ? 'ltr' : undefined}>{r.value}</span>
                        </div>
                    ))}
                </div>
                <BookingActions
                    appointmentId={booking.id}
                    status={booking.status}
                    allowedTransitions={booking.allowedTransitions}
                    onChanged={onClose}
                    actor={actor}
                />
                <div className={s.footer}>
                    {booking.canMarkPaid && onMarkPaid && (
                        <button type="button" className={s.actionBtn} onClick={onMarkPaid}>
                            {t('booking.action.markPaid')}
                        </button>
                    )}
                    {actor === 'owner' && footerAction}
                    <button type="button" className={s.ghostBtn} onClick={onClose}>
                        {t('calendar.peek.close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BookingDetailDialog;
